import { createTheme, alpha } from '@aviatrix/flights';

const categorical:Object = [
  {
    dark: '#363D63',
    medium: '#475285',
    main: '#596CA6',
    disabled: '#BDC4DB'
  },
  {
    dark: '#0566C7',
    medium: '#0680F9',
    main: '#38A9FA',
    disabled: '#9CD4FD'
  },
  {
    dark: '#BD660F',
    medium: '#EC8013',
    main: '#F08B42',
    disabled: '#F7C5A1'
  },
  {
    dark: '#4DB388',
    medium: '#70C2A0',
    main: '#93D1B2',
    disabled: '#C9E8D9'
  },
  {
    dark: '#3B19E5',
    medium: '#6347EB',
    main: '#8075F0',
    disabled: '#BFBAF7'
  },
  {
    dark: '#2E5C6B',
    medium: '#3D7A8F',
    main: '#4DA1B3',
    disabled: '#A6D0D9'
  },
  {
    dark: '#7A6952',
    medium: '#998466',
    main: '#AD9985',
    disabled: '#D6CCC2'
  },
  {
    dark: '#1F4447',
    medium: '#2E666B',
    main: '#3D8F8F',
    disabled: '#94D1D1'
  },
  {
    dark: '#AD1F2A',
    medium: '#D92635',
    main: '#E05269',
    disabled: '#F4BEC7'
  },
  {
    dark: '#E5A119',
    medium: '#EBB447',
    main: '#F0D175',
    disabled: '#F7E8BA'
  }
];

const semantic:Object = {
  green: {
    dark: '#467326',
    medium: '#659833',
    main: '#99CC66',
    disabled: '#CCE6B2'
  },
  yellow: {
    dark: '#916F08',
    medium: '#C2940A',
    main: '#F5B83D',
    disabled: '#FADB9E'
  },
  red: {
    dark: '#861D13',
    medium: '#B2261A',
    main: '#DB5757',
    disabled: '#F6D5D5'
  },
  grey: {
    dark: '#201E28',
    medium: '#625F68',
    main: '#8F8D96',
    disabled: '#E8E6EB'
  },
  orange: {
    dark: '#7E2101',
    medium: '#B12E02',
    main: '#E24402',
    disabled: '#FEDCCD'
  },
  blue: {
    dark: '#033363',
    medium: '#044D95',
    main: '#0578C7',
    disabled: '#8CABC1'
  }
};

const csp:Object = {
  aws: {
    main: '#FF9900',
    medium: '#CC6900',
    dark: '#994F00',
    disabled: '#FFD699'
  },
  arm: {
    main: '#00A4EF',
    medium: '#0071BD',
    dark: '#00538A',
    disabled: '#99DFFF'
  },
  gcp: {
    main: '#4285F4',
    medium: '#0E52F1',
    dark: '#0B42C1',
    disabled: '#CFE0FC'
  },
  oci: {
    main: '#C74634',
    medium: '#A22E2A',
    dark: '#7A221F',
    disabled: '#EAB5AE'
  },
  acs: {
    main: '#FF6A00',
    medium: '#CC4400',
    dark: '#994000',
    disabled: '#FFC499'
  },
  edge: {
    // same as copilot
    main: '#4E436F',
    medium: '#3B304F',
    dark: '#231D30',
    disabled: '#E2DFEC'
  },
  equinix: {
    // same as edge
    main: '#4E436F',
    medium: '#3B304F',
    dark: '#231D30',
    disabled: '#E2DFEC'
  },
  aviatrix: {
    // same as copilot
    main: '#4E436F',
    medium: '#3B304F',
    dark: '#231D30',
    disabled: '#E2DFEC'
  },
  unknown: {
    // same as grey
    main: '#8F8D96',
    medium: '#625F68',
    dark: '#201E28',
    disabled: '#E8E6EB'
  }
};

const createShadowPalette = (color:string) => ({
  small: `0px 2px 2px ${alpha(color, 0.02)}, 0px 4px 4px ${alpha(color, 0.02)}, 0px 8px 8px ${alpha(color, 0.02)}`,
  medium: `0px 2px 6px ${alpha(color, 0.16)}, 0px 0px 1px ${alpha(color, 0.16)}`,
  large: `0px 2px 8px ${alpha(color, 0.16)}, 0px 0px 2px ${alpha(color, 0.16)}`
});

const LIGHT_MODE_COLORS:any = {
  text: {
    dark: '#201E28',
    main: '#625F68',
    light: '#8F8D96',
    darkInverse: '#FFFFFF',
    mainInverse: '#F3F2F6',
    lightInverse: '#E8E6EB'
  },
  primary: {
    dark: '#033363',
    main: '#0578C7',
    light: '#CDEAFE'
  },
  secondary: {
    // from v4
    dark: '#9A0036',
    main: '#DC004E',
    light: '#E33371'
  },
  info: {
    dark: '#033363',
    medium: '#044D95',
    main: '#0578C7',
    light: '#CDEAFE',
    disabled: '#8CABC1',
    transparent: '#CDEAFE80'
  },
  success: {
    dark: '#2E4C19',
    medium: '#467326',
    main: '#659833',
    light: '#E5F2D8',
    transparent: '#E5F2D880'
  },
  warning: {
    dark: '#5C4E0A',
    medium: '#8A750F',
    main: '#B88F14',
    light: '#FAF0D1',
    transparent: '#FAF0D180'
  },
  error: {
    dark: '#591A0D',
    medium: '#862613',
    main: '#B2261A',
    light: '#F9D6D2',
    transparent: '#F9D6D280',
    disabled: '#B39B98'
  },
  border: {
    dark: '#625F68',
    main: '#D2D0D9',
    light: '#E8E6EB',
    medium: '#8F8D96'
  },
  background: {
    dark: '#D2D0D9',
    main: '#F3F2F6',
    light: '#FFFFFF',
    paper: '#FFFFFF',
    transparent: '#FFFFFFBF',
    medium: '#E8E6EB',
    disabled: '#EDECEF',
    overlay: '#201E2854',
    // This value dictates the background color of
    //  the application.
    default: '#F3F2F6'
  },
  aviatrix: {
    dark: '#7E2101',
    medium: '#B12E02',
    main: '#E24402',
    light: '#FEDCCD'
  },
  copilot: {
    main: '#4E436F',
    medium: '#3B304F',
    dark: '#231D30',
    light: '#E2DFEC'
  },
  categorical,
  semantic,
  csp,
  shadow: createShadowPalette('#201E28')
};

const DARK_MODE_COLORS:any = {
  primary: {
    main: '#f9fbe7'
  },
  secondary: {
    main: '#bbdefb'
  },
  text: {
    dark: '#4E436F',
    main: '#BBB9C3',
    light: '#79777F',
    darkInverse: '#36343D',
    mainInverse: '#4C4A54',
    lightInverse: '#625F68'
  },
  info: {
    dark: '#ABDDED',
    medium: '#81CBE4',
    main: '#57AFDB',
    light: '#123E54',
    disabled: '#627884',
    transparent: '#123E5480'
  },
  success: {
    dark: '#D0E5B3',
    medium: '#ACD98C',
    main: '#98CC65',
    light: '#334D19',
    transparent: '#334D1980'
  },
  warning: {
    dark: '#FAD49E',
    medium: '#F7BE6E',
    main: '#F5B83D',
    light: '#614205',
    transparent: '#61420580'
  },
  error: {
    dark: '#EDABB0',
    medium: '#E48189',
    main: '#DB5757',
    light: '#541212',
    transparent: '#54121280',
    disabled: '#846262'
  },
  border: {
    dark: '#79777F',
    main: '#4C4A54',
    light: '#36343D',
    medium: '#625F68'
  },
  background: {
    dark: '#79777F',
    main: '#2B2931',
    light: '#201E28',
    paper: '#201E28',
    transparent: '#201E28BF',
    medium: '#36343D',
    disabled: '#413E46',
    overlay: '#6f6f6f54',
    // This value dictates the background color of
    //  the application.
    default: '#2B2931'
  },
  aviatrix: {
    dark: '#FEC6B3',
    medium: '#FD7A4E',
    main: '#E24402',
    light: '#651F01'
  },
  copilot: {
    main: '#554D7F',
    medium: '#65609F',
    dark: '#8480B3',
    light: '#2B2640'
  },
  categorical,
  semantic,
  csp,
  shadow: createShadowPalette('#BBB9C3')
};

const TYPOGRAPHY:Object = {
  fontFamily: 'Source Sans Pro, Helvetica, Arial, sans-serif',

  h1: {
    fontSize: 32,
    lineHeight: '40px',
    fontWeight: 400
  },
  h2: {
    fontSize: 24,
    lineHeight: '32px',
    fontWeight: 400
  },
  h3: {
    fontSize: 20,
    lineHeight: '24px',
    fontWeight: 400
  },
  h4: {
    fontSize: 16,
    lineHeight: '20px',
    fontWeight: 400
  },
  h5: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 600
  },
  paragraph: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400
  },
  label: {
    fontSize: 12,
    lineHeight: '20px',
    fontWeight: 400
  },
  labelParagraph: {
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 400
  },
  console: {
    fontSize: 14,
    lineHeight: '18px',
    fontWeight: 400,
    fontFamily: 'Inconsolata'
  },
  tiny: {
    fontSize: 10,
    lineHeight: '12px',
    fontWeight: 400
  },
  // Overriding mui default with `paragraph`
  body1: {
    fontSize: 14,
    lineHeight: '21px',
    fontWeight: 400
  },
  // Overriding mui default with `paragraph`
  body2: {
    fontSize: 14,
    lineHeight: '21px',
    fontWeight: 400
  },
  button: {
    textTransform: 'none',
  }
};

/** @type {import('@mui/material').Theme['components']} */
const COMPONENTS:Object = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: 'Source Sans Pro, Helvetica, Arial, sans-serif'
      }
    }
  },
  MuiButtonBase: {
    defaultProps: {
      // https://mui.com/material-ui/getting-started/faq/#how-can-i-disable-the-ripple-effect-globally
      disableRipple: true,
    }
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        fontSize: 'inherit',
        height: 12,
        width: 12
      },
      fontSizeLarge: {
        height: 20,
        width: 20
      }
    }
  },
  MuiPaper:{
    styleOverrides: {
      root: {
        gap:0
      }
    }
  }
};

const fsTheme:any = {
  light: LIGHT_MODE_COLORS,
  dark: DARK_MODE_COLORS,
  typography: TYPOGRAPHY,
  components: COMPONENTS,
  categorical,
  semantic,
  csp,
  theme: {
    light: createTheme({
      palette: { ...LIGHT_MODE_COLORS, mode: 'light' },
      typography: TYPOGRAPHY,
      components: COMPONENTS
    }),
    dark: createTheme({
      palette: { ...DARK_MODE_COLORS, mode: 'dark' },
      typography: TYPOGRAPHY,
      components: COMPONENTS
    })
  }
};

export default fsTheme;
